import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  Chip,
  ListItemText,
} from '@mui/material';
import { BACKEND_URL } from '../../../utils/apiConfig';
import { authFetch } from '../../../utils/authHelpers';
import WijzigingslogKnop from '../../../ui-components/WijzigingslogKnop';
import { T, Icon, stitchInputSx, stitchPill, stitchSectionLabel, stitchGradientBtn, stitchOutlineBtn, stitchDialogPaper, StitchIconBtn } from './stitch';
import { statusColors, toFormaatArray, formatFormaat, formatDate } from './projectMeta';


// Keuzelijsten exact zoals in Airtable — een waarde die daar niet bestaat geeft
// een 422 bij het opslaan (backend patcht zonder typecast).
const DELIVERABLE_STATUS_OPTIONS = [
  'Nog niet gestart',
  'In progress',
  'Wachten op interne feedback',
  'Feedback ontvangen klant',
  'Feedback verwerken',
  'Opgeleverd',
];

const DELIVERABLE_FORMAAT_OPTIONS = ['16:9', '4:3', '1:1', '4:5', '9:16', 'Anders'];


// Deliverable Detail Dialog (with editing)
function DeliverableDetailDialog({ open, onClose, deliverable, onGenerateTasks, taskCount = 0, onUpdate }) {
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    if (deliverable) {
      setFormData({
        'Naam van Deliverable': deliverable.fields?.['Naam van Deliverable'] || '',
        'Status': deliverable.fields?.Status || 'Nog niet gestart',
        'Deadline': deliverable.fields?.Deadline || '',
        'Formaat': toFormaatArray(deliverable.fields?.Formaat),
        'LengteVideo': deliverable.fields?.LengteVideo || '',
        'Ondertitel nodig': deliverable.fields?.['Ondertitel nodig'] || false,
        'Thumbnail Nodig?': deliverable.fields?.['Thumbnail Nodig?'] || false,
        'Briefing': deliverable.fields?.Briefing || '',
        'Deadline Remarks': deliverable.fields?.['Deadline Remarks'] || '',
      });
      setIsEditing(false);
    }
  }, [deliverable]);

  if (!deliverable) return null;

  const fields = deliverable.fields || {};
  const name = fields['Naam van Deliverable'] || fields.Deliverable || 'Deliverable';
  const status = fields.Status || 'Nog niet gestart';
  const statusStyle = statusColors[status] || statusColors['Nog niet gestart'];

  const handleSave = async () => {
    setSaving(true);
    try {
      console.log('[Deliverable Save] Sending formData:', formData);
      const response = await authFetch(`${BACKEND_URL}/Api/deliverables/${deliverable.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const data = await response.json();
      console.log('[Deliverable Save] Response:', data);

      if (!response.ok) {
        const errorMsg = data.details?.error?.message || data.error || 'Kon deliverable niet opslaan';
        throw new Error(errorMsg);
      }

      // Update parent state
      onUpdate?.({ ...deliverable, fields: { ...fields, ...formData } });
      setIsEditing(false);
    } catch (err) {
      console.error('[Deliverable Save] Error:', err);
      toast.error(`Fout bij opslaan: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: stitchDialogPaper }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1 }}>
            {isEditing ? (
              <TextField
                value={formData['Naam van Deliverable']}
                onChange={(e) => setFormData(prev => ({ ...prev, 'Naam van Deliverable': e.target.value }))}
                variant="standard"
                fullWidth
                InputProps={{ sx: { fontSize: '1.25rem', fontFamily: T.font, fontWeight: 700 } }}
              />
            ) : (
              <Typography variant="h6" sx={{ fontFamily: T.font, fontWeight: 700 }}>{name}</Typography>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              {isEditing ? (
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <Select
                    value={formData['Status']}
                    onChange={(e) => setFormData(prev => ({ ...prev, 'Status': e.target.value }))}
                    sx={{ fontFamily: T.fontBody }}
                  >
                    {DELIVERABLE_STATUS_OPTIONS.map(opt => (
                      <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <Typography component="span" sx={stitchPill(statusStyle.text, statusStyle.bg)}>
                  {status}
                </Typography>
              )}
              {taskCount > 0 && (
                <Typography component="span" sx={{ ...stitchPill(T.outline, 'transparent'), border: `1px solid ${T.outlineVariant}` }}>
                  {`${taskCount} taken`}
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {deliverable?.id && (
              <WijzigingslogKnop entityId={deliverable.id} titel={deliverable?.fields?.['Naam van Deliverable'] || ''}
                renderKnop={(open) => (
                  <StitchIconBtn onClick={open} title="Wijzigingslog: wie wijzigde wat, wanneer">
                    <Icon name="history" size={20} />
                  </StitchIconBtn>
                )} />
            )}
            {!isEditing && (
              <StitchIconBtn onClick={() => setIsEditing(true)} title="Bewerken">
                <Icon name="edit" size={20} />
              </StitchIconBtn>
            )}
            <StitchIconBtn onClick={onClose} title="Sluiten">
              <Icon name="close" size={20} />
            </StitchIconBtn>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
          {/* Basic Info */}
          <Box>
            <Typography sx={stitchSectionLabel}>
              Algemene info
            </Typography>
            <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Deadline */}
              {isEditing ? (
                <TextField
                  label="Deadline"
                  type="date"
                  value={formData['Deadline']}
                  onChange={(e) => setFormData(prev => ({ ...prev, 'Deadline': e.target.value }))}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  sx={stitchInputSx}
                />
              ) : fields.Deadline ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Icon name="calendar_today" size={18} sx={{ color: T.outline }} />
                  <Box>
                    <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outline }}>Deadline</Typography>
                    <Typography variant="body2" sx={{ fontFamily: T.fontBody, fontWeight: 500 }}>{formatDate(fields.Deadline)}</Typography>
                  </Box>
                </Box>
              ) : isEditing ? null : (
                <Typography variant="body2" sx={{ fontFamily: T.fontBody, color: T.outline }}>Geen deadline</Typography>
              )}

              {/* Formaat */}
              {isEditing ? (
                <FormControl size="small" fullWidth sx={stitchInputSx}>
                  <InputLabel>Formaat</InputLabel>
                  <Select
                    multiple
                    value={toFormaatArray(formData['Formaat'])}
                    label="Formaat"
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      'Formaat': typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value,
                    }))}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map(val => (
                          <Chip key={val} label={val} size="small" sx={{ fontFamily: T.fontBody }} />
                        ))}
                      </Box>
                    )}
                  >
                    {DELIVERABLE_FORMAAT_OPTIONS.map(opt => (
                      <MenuItem key={opt} value={opt}>
                        <Checkbox checked={toFormaatArray(formData['Formaat']).includes(opt)} size="small" />
                        <ListItemText primary={opt} primaryTypographyProps={{ fontFamily: T.fontBody }} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : formatFormaat(fields.Formaat) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 18, textAlign: 'center' }}>{'\u{1F4D0}'}</Box>
                  <Box>
                    <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outline }}>Formaat</Typography>
                    <Typography variant="body2" sx={{ fontFamily: T.fontBody, fontWeight: 500 }}>{formatFormaat(fields.Formaat)}</Typography>
                  </Box>
                </Box>
              )}

              {/* Lengte */}
              {isEditing ? (
                <TextField
                  label="Lengte (seconden)"
                  type="number"
                  value={formData['LengteVideo']}
                  onChange={(e) => setFormData(prev => ({ ...prev, 'LengteVideo': e.target.value }))}
                  size="small"
                  fullWidth
                  sx={stitchInputSx}
                />
              ) : fields.LengteVideo && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 18, textAlign: 'center' }}>{'\u23F1'}</Box>
                  <Box>
                    <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outline }}>Lengte</Typography>
                    <Typography variant="body2" sx={{ fontFamily: T.fontBody, fontWeight: 500 }}>{fields.LengteVideo} seconden</Typography>
                  </Box>
                </Box>
              )}

              {/* Ondertiteling & Thumbnail checkboxes */}
              {isEditing && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData['Ondertitel nodig'] || false}
                        onChange={(e) => setFormData(prev => ({ ...prev, 'Ondertitel nodig': e.target.checked }))}
                        size="small"
                      />
                    }
                    label={<Typography variant="body2" sx={{ fontFamily: T.fontBody }}>{'\u{1F524}'} Ondertiteling nodig</Typography>}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData['Thumbnail Nodig?'] || false}
                        onChange={(e) => setFormData(prev => ({ ...prev, 'Thumbnail Nodig?': e.target.checked }))}
                        size="small"
                      />
                    }
                    label={<Typography variant="body2" sx={{ fontFamily: T.fontBody }}>{'\u{1F5BC}\uFE0F'} Thumbnail nodig</Typography>}
                  />
                </Box>
              )}

              {/* Show checkboxes status in view mode - always visible */}
              {!isEditing && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Typography component="span" sx={stitchPill(
                    fields['Ondertitel nodig'] ? '#1d4ed8' : T.outline,
                    fields['Ondertitel nodig'] ? '#dbeafe' : T.surfaceLow
                  )}>
                    {'\u{1F524}'} Ondertiteling {fields['Ondertitel nodig'] ? '\u2713' : '\u2717'}
                  </Typography>
                  <Typography component="span" sx={stitchPill(
                    fields['Thumbnail Nodig?'] ? '#be185d' : T.outline,
                    fields['Thumbnail Nodig?'] ? '#fce7f3' : T.surfaceLow
                  )}>
                    {'\u{1F5BC}\uFE0F'} Thumbnail {fields['Thumbnail Nodig?'] ? '\u2713' : '\u2717'}
                  </Typography>
                </Box>
              )}

              {fields.Type && !isEditing && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Icon name="movie" size={18} sx={{ color: T.outline }} />
                  <Box>
                    <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outline }}>Type</Typography>
                    <Typography variant="body2" sx={{ fontFamily: T.fontBody, fontWeight: 500 }}>{Array.isArray(fields.Type) ? fields.Type.join(', ') : fields.Type}</Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>

          {/* Notities - editable in edit mode, read-only otherwise */}
          <Box>
            <Typography sx={stitchSectionLabel}>
              Notities
            </Typography>
            <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {isEditing ? (
                <>
                  <TextField
                    label="Briefing"
                    value={formData['Briefing'] || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, 'Briefing': e.target.value }))}
                    size="small"
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Beschrijving van de deliverable, context, etc."
                    sx={stitchInputSx}
                  />
                  <TextField
                    label="Deadline opmerkingen"
                    value={formData['Deadline Remarks'] || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, 'Deadline Remarks': e.target.value }))}
                    size="small"
                    fullWidth
                    multiline
                    rows={2}
                    placeholder="Opmerkingen over de deadline..."
                    sx={{ ...stitchInputSx, '& .MuiOutlinedInput-root': { ...stitchInputSx['& .MuiOutlinedInput-root'], backgroundColor: T.amberBg } }}
                  />
                </>
              ) : (
                <>
                  {fields.Briefing && (
                    <Box>
                      <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outline }}>Briefing</Typography>
                      <Typography variant="body2" sx={{ fontFamily: T.fontBody, mt: 0.5, p: 1.5, backgroundColor: T.surfaceLow, borderRadius: '0.5rem', whiteSpace: 'pre-wrap' }}>
                        {fields.Briefing}
                      </Typography>
                    </Box>
                  )}
                  {fields['Deadline Remarks'] && (
                    <Box>
                      <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outline }}>Deadline opmerkingen</Typography>
                      <Typography variant="body2" sx={{ fontFamily: T.fontBody, mt: 0.5, p: 1.5, backgroundColor: T.amberBg, borderRadius: '0.5rem', whiteSpace: 'pre-wrap' }}>
                        {fields['Deadline Remarks']}
                      </Typography>
                    </Box>
                  )}
                  {!fields.Briefing && !fields['Deadline Remarks'] && (
                    <Typography variant="body2" sx={{ fontFamily: T.fontBody, color: T.outline, fontStyle: 'italic' }}>
                      Geen notities. Klik op bewerken om toe te voegen.
                    </Typography>
                  )}
                </>
              )}
            </Box>
          </Box>
        </Box>

        {/* Extra fields (read-only) */}
        {!isEditing && (fields['Speelduur final cut'] || fields['Aantal revisies']) && (
          <Box sx={{ mt: 3, pt: 3, borderTop: `1px solid ${T.surfaceHigh}` }}>
            <Typography sx={stitchSectionLabel}>
              Extra details
            </Typography>
            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {fields['Speelduur final cut'] && (
                <Typography component="span" sx={{ ...stitchPill(T.outline, 'transparent'), border: `1px solid ${T.outlineVariant}` }}>
                  Final cut: {fields['Speelduur final cut']}s
                </Typography>
              )}
              {fields['Aantal revisies'] && (
                <Typography component="span" sx={{ ...stitchPill(T.outline, 'transparent'), border: `1px solid ${T.outlineVariant}` }}>
                  {fields['Aantal revisies']} revisies
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
        <Box>
          {isEditing && (
            <Box component="span" onClick={() => setIsEditing(false)} sx={{ ...stitchOutlineBtn, opacity: saving ? 0.5 : 1 }}>
              Annuleren
            </Box>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {isEditing ? (
            <Box
              component="span"
              onClick={saving ? undefined : handleSave}
              sx={{ ...stitchGradientBtn, opacity: saving ? 0.7 : 1 }}
            >
              {saving ? 'Opslaan...' : 'Opslaan'}
            </Box>
          ) : (
            <>
              <Box component="span" onClick={onClose} sx={stitchOutlineBtn}>Sluiten</Box>
              <Box
                component="span"
                onClick={() => {
                  onClose();
                  onGenerateTasks?.(deliverable);
                }}
                sx={stitchGradientBtn}
              >
                <Icon name="auto_awesome" size={18} />
                Planning genereren
              </Box>
            </>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
}

export default DeliverableDetailDialog;
